import type { FunctionComponent } from 'react';
import { Fragment, useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Badge } from '~/ui/core/badge/Badge';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';

import type { JobItemDetails } from '../../api/use-job-item-details';
import { useArchivedItemActions } from '../../hooks/use-archived-item-actions';
import { PromptShell } from './PromptShell';

type ItemType = 'job-level' | 'job-role';

export const ArchivedPrompt: FunctionComponent<{
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: ItemType;
    readonly jobType: string;
}> = ({ architectureId, item, itemType, jobType }) => {
    const { translate } = useContext(ApplicationContext);
    const { handleDelete, handleRestore, isDeleteDialogOpen, setIsDeleteDialogOpen } = useArchivedItemActions({
        architectureId,
        item,
        itemType,
    });

    const handleRestoreClick = useCallback((): void => {
        void handleRestore();
    }, [handleRestore]);

    const handleDeleteClick = useCallback((): void => {
        setIsDeleteDialogOpen(true);
    }, [setIsDeleteDialogOpen]);

    return (
        <Fragment>
            <PromptShell
                question={translate('pages.job-architecture-v2.prompts.archived-question', { jobType })}
                actions={
                    <>
                        <Badge variant="success" onClick={handleRestoreClick}>
                            {translate('pages.job-architecture-v2.restore')}
                        </Badge>
                        <Badge variant="error" onClick={handleDeleteClick}>
                            {translate('pages.job-architecture-v2.prompts.delete-permanently')}
                        </Badge>
                    </>
                }
            />
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('pages.job-architecture-v2.delete-permanently-description')}
                open={isDeleteDialogOpen}
                title={translate('pages.job-architecture-v2.delete-permanently-title')}
                onCancel={() => setIsDeleteDialogOpen(false)}
                onConfirm={() => void handleDelete()}
            />
        </Fragment>
    );
};
